import type { EnterAuthUser } from '../types';

export class GatewayUnauthorizedError extends Error {
  constructor() {
    super('Gateway session is not authenticated');
    this.name = 'GatewayUnauthorizedError';
  }
}

export class GatewaySilentLoginRequiredError extends Error {
  constructor() {
    super('Gateway requires silent login recovery');
    this.name = 'GatewaySilentLoginRequiredError';
  }
}

export type GatewaySessionErrorCategory =
  | 'network'
  | 'aborted'
  | 'server'
  | 'http'
  | 'invalid_response';

export class GatewaySessionRequestError extends Error {
  readonly name = 'GatewaySessionRequestError';

  constructor(
    message: string,
    readonly category: GatewaySessionErrorCategory,
    readonly status?: number,
    readonly originalError?: unknown
  ) {
    super(message);
  }
}

export interface GatewayAccessToken {
  accessToken: string;
  expiresAt: number;
}

export type GatewaySession =
  | {
      status: 'anonymous';
    }
  | {
      status: 'authenticated';
      user: EnterAuthUser;
      accessToken?: string;
      expiresAt?: number;
    };

const GATEWAY_SESSION_REQUEST_TIMEOUT_MS = 5_000;

function requestOptions(signal?: AbortSignal): RequestInit {
  return {
    cache: 'no-store',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal,
  };
}

function toRequestError(error: unknown, aborted = false): GatewaySessionRequestError {
  const category =
    aborted || (error instanceof DOMException && error.name === 'AbortError')
      ? 'aborted'
      : 'network';
  return new GatewaySessionRequestError(
    category === 'aborted'
      ? 'Gateway session request was aborted'
      : 'Gateway session request failed',
    category,
    undefined,
    error
  );
}

async function parseSessionJson(
  response: Response,
  signal?: AbortSignal
): Promise<Record<string, unknown>> {
  try {
    const body: unknown = await response.json();
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
      throw new TypeError('Gateway session response must be an object');
    }
    return body as Record<string, unknown>;
  } catch (error) {
    if (signal?.aborted) {
      throw toRequestError(error, true);
    }
    throw new GatewaySessionRequestError(
      'Gateway session response was not valid JSON',
      'invalid_response',
      response.status,
      error
    );
  }
}

function isEnterAuthUser(value: unknown): value is EnterAuthUser {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { sub?: unknown }).sub === 'string'
  );
}

/**
 * 读取 Cookie 所代表的服务端登录态；只有 204/401 明确表示匿名。
 * 首次挂载可显式请求 token，后续登录态校验保持轻量，不重复传输 bearer token。
 */
export async function fetchGatewaySession(options?: {
  includeAccessToken?: boolean;
  signal?: AbortSignal;
  timeoutMs?: number;
}): Promise<GatewaySession> {
  const sessionUrl = options?.includeAccessToken
    ? '/auth/session?include=access_token'
    : '/auth/session';
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  if (options?.signal?.aborted) {
    controller.abort();
  } else {
    options?.signal?.addEventListener('abort', abortFromCaller, { once: true });
  }
  const timeoutId = setTimeout(
    () => controller.abort(),
    options?.timeoutMs ?? GATEWAY_SESSION_REQUEST_TIMEOUT_MS
  );

  try {
    let response: Response;
    try {
      response = await fetch(sessionUrl, requestOptions(controller.signal));
    } catch (error) {
      throw toRequestError(error, controller.signal.aborted);
    }

    if (response.status === 204 || response.status === 401) {
      return { status: 'anonymous' };
    }
    if (response.status === 409) {
      const body = await parseSessionJson(response, controller.signal);
      if (body.code === 'CENTER_SESSION_SILENT_LOGIN_REQUIRED') {
        throw new GatewaySilentLoginRequiredError();
      }
    }
    if (!response.ok) {
      throw new GatewaySessionRequestError(
        `Failed to read Gateway session (${response.status})`,
        response.status >= 500 ? 'server' : 'http',
        response.status
      );
    }

    const body = await parseSessionJson(response, controller.signal);
    if (!isEnterAuthUser(body.user)) {
      throw new GatewaySessionRequestError(
        'Authenticated Gateway session did not include a valid user',
        'invalid_response',
        response.status
      );
    }

    return {
      status: 'authenticated',
      user: body.user,
      accessToken: typeof body.accessToken === 'string' ? body.accessToken : undefined,
      expiresAt: typeof body.expiresAt === 'number' ? body.expiresAt : undefined,
    };
  } finally {
    clearTimeout(timeoutId);
    options?.signal?.removeEventListener('abort', abortFromCaller);
  }
}

/**
 * Token 不持久化到浏览器。Provider 仅短期保留此结果，并定期重新经 Gateway
 * 检查 HttpOnly session；本地内存状态不会成为跨平台登出的授权依据。
 */
export async function fetchGatewayAccessToken(): Promise<GatewayAccessToken> {
  const response = await fetch('/auth/access-token', requestOptions());

  if (response.status === 401) {
    throw new GatewayUnauthorizedError();
  }
  if (!response.ok) {
    throw new Error(`Failed to read Gateway access token (${response.status})`);
  }

  return await response.json() as GatewayAccessToken;
}


// ─── Bootstrap 预热（warm/consume） ─────────────────────────────────────────
//
// 首个 session 读取与应用渲染天然并行：入口（main.tsx）在加载 app-runtime 的
// 同时调用 warmGatewaySessionForBootstrap() 提前发起请求；session-provider
// 首次 bootstrap 时通过 consumeWarmGatewaySession() 复用同一在途 Promise。
// provider 无预热可用时仍按原路径自发加载，两种触发点共用本文件的加载实现，
// 单一事实源，不新增第二套 session 读取逻辑。

let pendingBootstrapSession: Promise<GatewaySession> | null = null;

/**
 * 提前发起首个 session 读取（幂等）。只发起、不消费。
 * 预热失败不替代 provider 的重试语义：清空后可由 provider 重新发起。
 */
export function warmGatewaySessionForBootstrap(): void {
  if (pendingBootstrapSession) {
    return;
  }
  const pending = fetchGatewaySession({ includeAccessToken: true });
  // 槽位必须存原始 Promise：.catch() 按规范必然返回新 Promise，若存派生
  // Promise，下面的身份比较恒为 false，失败后槽位永远清不掉（死代码）。
  pendingBootstrapSession = pending;
  // 失败自愈：清空槽位，让 provider / 后续 warm 能按原路径重新发起。
  // 同时充当 provider 消费前的 unhandled rejection 兜底。
  void pending.catch(() => {
    if (pendingBootstrapSession === pending) {
      pendingBootstrapSession = null;
    }
  });
}

/**
 * session-provider 首次 bootstrap 加载时调用；最多消费一次。
 * 返回 undefined 表示没有可复用的预热请求，调用方按原路径自发加载。
 */
export function consumeWarmGatewaySession(): Promise<GatewaySession> | undefined {
  const pending = pendingBootstrapSession;
  pendingBootstrapSession = null;
  return pending ?? undefined;
}

/** Test-only: 清空预热状态，避免用例间串扰。 */
export function __resetWarmGatewaySessionForTests(): void {
  pendingBootstrapSession = null;
}
